<template>
  <div>
    <b-table
      :id="id"
      category="AudienceSegmentTable"
      :columns="columns"
      :items="segments"
      :pagination="true"
      :show-filters="true"
      :total-count="totalCount"
      :enable-client-side-sorting="false"
      :enable-column-reorder="false"
      :is-loading="isLoading"
      :cast-shadow="false"
      :no-padding="true"
      :enable-search-section="false"
      @page-index-changed="updatePageIndex"
      @page-size-changed="updatePageSize"
      @filters-changed="onFilterChange">
      <template #link="item">
        <a
          class="segment-link"
          @click="selectSegment(item)"
          >{{ item.item.name }}</a
        >
      </template>
      <template #reach="item">
        <b>{{ $n(item.item.reach) }}</b>
      </template>
      <template #category="item">
        {{ capitalize(getCategoryName(item.item.category)) }} ({{ getCategory(item.item.category) }})
      </template>
    </b-table>
  </div>
</template>
<script setup lang="ts">
  import { capitalize, computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import BTable from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import { DataProviderCodes } from '@/common/enums/data-provider-codes';
  import DemographicCategory from '@/common/enums/DemographicCategory.enum';
  import {
    DataProvider,
    Filter,
    FilterCollection,
    FilterComparisonOperator,
    FilterGroup,
    FilterLogicalOperator,
    QueryParams,
  } from '@/common/models';
  import ExternalSegment from '@/common/models/audience/ExternalSegment';
  import ExternalSegmentType from '@/common/models/audience/ExternalSegmentType';
  import { snakeToCamel } from '@/globals';
  import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';
  import { mapOrgAppActions } from '@/utils/StateMapping';

  interface Props {
    id: string;
    segmentType?: ExternalSegmentType;
    searchTerm?: string;
    showCategoryCol?: boolean;
    dataProvider: DataProvider;
  }
  interface Emits {
    (e: 'selectAudienceSegment', item: ExternalSegment): void;
  }
  const props = defineProps<Props>();
  const emits = defineEmits<Emits>();
  const i18n = useI18n();
  const { getExternalSegmentsByOrg, getExternalCountriesAndCategories } = mapOrgAppActions();

  // Computed
  const categoryFilter = computed(() => {
    if (!props.segmentType) return null;
    const value =
      props.dataProvider.code === DataProviderCodes.EPSILON
        ? AudienceSegmentUtils.epsilonSegmentKeyValuePairs()[props.segmentType.name]
        : props.segmentType.grouping;
    return new Filter('category', value);
  });
  const demographicsFilter = computed(
    () =>
      new Filter('category', [DemographicCategory.Age, DemographicCategory.Gender], FilterComparisonOperator.NOT_IN),
  );
  const columns = computed(() => ({
    link: TableColumn.fromObject({
      label: capitalize(i18n.t('names.segmentName')),
      property: 'name',
      type: TableColumnTypes.string,
    }),
    category: TableColumn.fromObject({
      label: capitalize(i18n.t('names.category')),
      property: 'category',
      type: TableColumnTypes.string,
      source: () => getCategoriesOrLocations('categories'),
      sourceFilterProperty: 'category',
      sourceLabel: 'label',
      sourceID: 'id',
    }),
    country: TableColumn.fromObject({
      label: capitalize(i18n.t('names.country')),
      property: 'location.code',
      type: TableColumnTypes.string,
      source: () => getCategoriesOrLocations('locations'),
      sourceFilterProperty: 'location.code',
      sourceLabel: 'name',
      sourceID: 'code',
    }),
    description: TableColumn.fromObject({
      label: capitalize(i18n.t('names.description')),
      property: 'description',
      type: TableColumnTypes.string,
    }),
    reach: TableColumn.fromObject({
      label: capitalize(i18n.t('names.reach')),
      property: 'reach',
      type: TableColumnTypes.integer,
      right: true,
      description: i18n.t('messages.reach'),
    }),
    cpmFormatted: TableColumn.fromObject({
      label: capitalize(i18n.t('names.cpm')),
      property: 'cpm',
      type: TableColumnTypes.currency,
    }),
    externalId: TableColumn.fromObject({
      label: capitalize(i18n.t('names.lifesightSegmentId')),
      type: TableColumnTypes.string,
      property: 'external_id',
      useAsFilter: true,
      sourceFilterProperty: 'external_id',
      shouldRenderColumn: false,
    }),
  }));

  // Reactive state
  const isLoading = ref(true);
  const metadata = ref(null);
  const pageIndex = ref(0);
  const pageSize = ref(10);
  const segments = ref<ExternalSegment[]>([]);
  const totalCount = ref(0);
  let queryParams: QueryParams = null;

  // Helpers
  function updatePageIndex(idx: number) {
    pageIndex.value = idx;
  }
  function updatePageSize(val: number) {
    pageSize.value = val;
  }
  function selectSegment({ item }: { item: ExternalSegment }) {
    emits('selectAudienceSegment', item);
  }
  function getCategory(name: string) {
    return AudienceSegmentUtils.getHsCategoryByLifesightCategory(name);
  }
  function getCategoryName(key: string) {
    const keyNoSpaces = key.toLowerCase().replace(' ', '_');
    return capitalize(i18n.t(`names.${snakeToCamel(keyNoSpaces)}`));
  }
  function formattedCategoriesMetada() {
    return metadata.value.categories.map((c) => ({
      id: c,
      label: getCategoryName(c),
    }));
  }
  function onFilterChange(qp: QueryParams) {
    const filters = [...qp.filterCollection.filters, demographicsFilter.value];

    if (categoryFilter.value) {
      filters.push(categoryFilter.value);
    }

    if (props.searchTerm) {
      filters.push(
        new FilterGroup(
          [
            new Filter('name', [props.searchTerm], FilterComparisonOperator.LIKE),
            new Filter('description', [props.searchTerm], FilterComparisonOperator.LIKE),
            new Filter('external_id', [props.searchTerm], FilterComparisonOperator.EQUAL),
          ],
          FilterLogicalOperator.OR,
        ),
      );
    }

    queryParams.skip = qp.skip;
    queryParams.top = qp.top;
    queryParams.sort = qp.sort;
    queryParams.filterCollection = new FilterCollection(filters);

    fetchExternalSegments();
  }

  // Data fetch
  async function getCategoriesOrLocations(type: string) {
    if (['categories', 'locations'].indexOf(type) === -1) return [];
    if (metadata.value && type in metadata.value) {
      return type === 'categories' ? formattedCategoriesMetada() : metadata.value[type];
    }

    const queryParams = new QueryParams();
    queryParams.customQueryParams.dataProviderId = props.dataProvider.id;
    queryParams.showLoader = false;
    metadata.value = await queryParams.run(getExternalCountriesAndCategories);

    if (type === 'categories') {
      return formattedCategoriesMetada();
    }

    return metadata.value[type];
  }
  async function fetchExternalSegments() {
    queryParams.customQueryParams.dataProviderId = props.dataProvider.id;
    queryParams.showLoader = false;
    try {
      const res = await queryParams.run(getExternalSegmentsByOrg);
      if (res) {
        segments.value = res.items;
        totalCount.value = res.totalCount;
      }
    } finally {
      isLoading.value = false;
    }
  }

  onMounted(async () => {
    const filterCollection = new FilterCollection([demographicsFilter.value, AudienceSegmentUtils.enabledFilter]);
    if (categoryFilter.value) {
      filterCollection.addFilter(categoryFilter.value);
    }
    if (props.searchTerm) {
      filterCollection.addFilter(
        new FilterGroup(
          [
            new Filter('name', [props.searchTerm], FilterComparisonOperator.LIKE),
            new Filter('description', [props.searchTerm], FilterComparisonOperator.LIKE),
            new Filter('external_id', [props.searchTerm], FilterComparisonOperator.EQUAL),
          ],
          FilterLogicalOperator.OR,
        ),
      );
    }
    queryParams = new QueryParams(pageSize.value, 0, filterCollection, 'reach desc', true, ['location']);
    await fetchExternalSegments();
  });
</script>
